import { useCallback, useEffect, useRef, useState } from 'react';
import PageBackground from '@/components/base/PageBackground';
import { MAINTENANCE_INFO } from '@/config/site-status';

export default function ServerErrorPage() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const handleCopyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(MAINTENANCE_INFO.supportEmail);
      setCopied(true);
    } catch {
      setCopied(false);
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 2000);
  }, []);

  return (
    <PageBackground>
      <div className="relative flex min-h-screen w-full flex-col">
        <header className="flex w-full items-center justify-between border-b border-white/5 px-6 py-5 md:px-10">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-rp-orange/15 text-rp-glow-soft">
              <i className="ri-database-2-line text-lg leading-none" />
            </span>
            <span className="text-[15px] font-semibold tracking-tight text-white">
              {MAINTENANCE_INFO.brandName}
            </span>
          </div>

          <span className="flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-400/10 px-3.5 py-1.5 text-[11px] font-medium text-amber-300 whitespace-nowrap md:text-xs">
            <span className="relative flex h-2 w-2 items-center justify-center">
              <span className="absolute h-2 w-2 animate-ping rounded-full bg-amber-400/50" />
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
            </span>
            {MAINTENANCE_INFO.statusLabel}
          </span>
        </header>

        <main className="flex flex-1 flex-col justify-center px-6 py-12 md:px-10 md:py-16">
          <div className="mx-auto w-full max-w-[1180px]">
            <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
              {/* Text block */}
              <div>
                <div
                  className="mb-5 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-1.5"
                  style={{ animation: 'textReveal 0.7s ease-out both' }}
                >
                  <i className="ri-alert-line text-sm leading-none text-amber-400" />
                  <span className="font-mono text-[11px] tracking-wide text-slate-400">
                    {MAINTENANCE_INFO.errorCode}
                  </span>
                </div>

                <h1
                  className="text-[28px] font-bold leading-[1.22] tracking-tight text-white md:text-[42px]"
                  style={{ animation: 'textReveal 0.8s ease-out 0.1s both' }}
                >
                  서버에 일시적인
                  <br />
                  <span className="text-gradient-blue">오류가 발생했습니다</span>
                </h1>

                <p
                  className="mt-5 max-w-[520px] text-[13.5px] leading-[1.75] text-slate-400 md:text-[15px]"
                  style={{ animation: 'textReveal 0.8s ease-out 0.2s both' }}
                >
                  {MAINTENANCE_INFO.description}
                </p>

                <div
                  className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
                  style={{ animation: 'textReveal 0.8s ease-out 0.3s both' }}
                >
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-rp-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-rp-orange-dark whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-base leading-none" />
                    다시 시도
                  </button>

                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-white/20 hover:bg-white/10 whitespace-nowrap"
                  >
                    <i
                      className={`text-base leading-none ${copied ? 'ri-check-line text-emerald-400' : 'ri-mail-line'}`}
                    />
                    {copied ? '이메일이 복사됐어요' : '문의 이메일 복사'}
                  </button>
                </div>

                <p
                  className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-slate-500"
                  style={{ animation: 'textReveal 0.8s ease-out 0.4s both' }}
                >
                  <i className="ri-shield-check-line mt-0.5 text-sm leading-none text-rp-glow-soft" />
                  <span>
                    스테이킹한 자산과 기존 기록은 그대로 유지됩니다. 안내 없이 지갑 정보나 개인키를
                    요구하는 연락은 사칭이니 주의해 주세요.
                  </span>
                </p>
              </div>

              {/* Illustration */}
              <div
                className="relative"
                style={{ animation: 'textReveal 0.9s ease-out 0.25s both' }}
              >
                <div
                  className="absolute inset-6 rounded-full blur-3xl"
                  style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.28) 0%, transparent 70%)' }}
                />
                <div className="relative w-full h-[260px] overflow-hidden rounded-lg border border-white/10 bg-rp-navy-light/60 md:h-[380px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylized%203D%20illustration%20of%20a%20damaged%20server%20rack%20with%20a%20glowing%20cyan%20warning%20triangle%20and%20floating%20broken%20data%20particles%2C%20dark%20navy%20background%20with%20soft%20blue%20light%20beams%2C%20minimal%20clean%20technology%20aesthetic%2C%20high%20detail%20render%2C%20harmonious%20composition%2C%20no%20text&width=1200&height=800&seq=server-error-hero-01&orientation=landscape"
                    alt="서버 오류를 표현한 3D 일러스트"
                    title="서버 오류 안내 일러스트"
                    className="w-full h-full object-cover object-top animate-float-soft"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-rp-bg-dark/80 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md border border-white/10 bg-rp-bg-dark/80 px-3 py-1.5 backdrop-blur">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <span className="text-[11px] font-medium text-slate-300 whitespace-nowrap">
                      복구 진행률 모니터링 중
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status panels */}
            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:mt-16">
              {MAINTENANCE_INFO.panels.map((panel, index) => (
                <div
                  key={panel.label}
                  className="rounded-lg border border-white/8 bg-rp-navy-light/50 p-5 transition-colors hover:border-rp-orange/30"
                  style={{ animation: `textReveal 0.7s ease-out ${0.45 + index * 0.08}s both` }}
                >
                  <div className="mb-3 flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-rp-orange/12 text-rp-glow-soft">
                      <i className={`${panel.icon} text-sm leading-none`} />
                    </span>
                    <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                      {panel.label}
                    </span>
                  </div>
                  <p className="text-[15px] font-semibold text-white">{panel.value}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{panel.caption}</p>
                </div>
              ))}
            </div>
          </div>
        </main>

        <footer className="border-t border-white/5 px-6 py-5 md:px-10">
          <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span className="whitespace-nowrap">
              © 2026 {MAINTENANCE_INFO.brandName}. 서비스 복구 후 정상 운영됩니다.
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <i className="ri-mail-send-line text-sm leading-none text-slate-400" />
              {MAINTENANCE_INFO.supportEmail}
            </span>
          </div>
        </footer>
      </div>
    </PageBackground>
  );
}